import { defineStore } from 'pinia';

import { useTaskStore } from '@/store';

export type LauncherCategory = 'view_all_tasks' | 'view_invoices' | 'view_partners';

export type ViewTypes = 'modal' | 'sidebar' | 'sidepanel';

export type LauncherAction = 'back' | 'create_template' | 'create_workflow';

interface State {
  isChatEnabled: boolean;
  isDisabled: boolean;
  isLauncherOpen: boolean;
  isNavMenuOpen: boolean;
  launcherCategory: LauncherCategory | null;
  viewType: ViewTypes | null;
}

export const useLauncherStore = defineStore('launcher', {
  actions: {
    closeChat() {
      this.isLauncherOpen = false;
      this.viewType = null;
    },

    closeNavMenu() {
      this.$patch({ isNavMenuOpen: false });
    },

    disable() {
      this.isDisabled = true;
    },

    enable() {
      this.isDisabled = false;
    },

    fetchTasks() {
      const taskStore = useTaskStore();

      return taskStore.fetchTasks();
    },

    open(viewType?: ViewTypes) {
      if (this.isDisabled) {
        console.warn('Chat Assistant is currently disabled');
        return;
      }

      this.$patch({
        isLauncherOpen: true,
        viewType: viewType || 'modal',
      });
    },

    openChatModal() {
      this.open('modal');
    },

    openChatSidebar() {
      this.open('sidebar');
    },

    openChatSidepanel() {
      this.open('sidepanel');
    },

    openNavMenu() {
      this.$patch({ isNavMenuOpen: true });
    },

    toggleChat() {
      this.isLauncherOpen = !this.isLauncherOpen;
    },

    toggleNavMenu() {
      this.$patch({ isNavMenuOpen: !this.isNavMenuOpen });
    },

    verifyChatEnabled(profile) {
      const isDevEnv = process.env.NODE_ENV !== 'production';
      const isAuthorizedOrg =
        profile?.organization?.id &&
        [
          // PRODUCATION_ENV Acme + Stuut
          'org_19BqiTiDrr5Tq7pkQCQWy3',

          // DEVELOPMENT_ENV Acme + Stuut
          'org_7BaC8FaHyjeohknOsz9MAX',
          'org_nO5j6KzECCRpJh7vU', // Bishop
          'org_tIpr7t7fLBPp8r8A4',
        ].includes(profile?.organization?.id);

      this.$patch({ isChatEnabled: isDevEnv || isAuthorizedOrg });
    },
  },

  getters: {
    isOpenModal: (state) => state.isLauncherOpen && state.viewType === 'modal',
    isOpenSidebar: (state) => state.isLauncherOpen && state.viewType === 'sidebar',
    isOpenSideLayout: (state) =>
      state.isLauncherOpen && (state.viewType === 'sidebar' || state.viewType === 'sidepanel'),
    isOpenSidepanel: (state) => state.isLauncherOpen && state.viewType === 'sidepanel',
    isTypeModal: (state) => state.viewType === 'modal',
    isTypeSidebar: (state) => state.viewType === 'sidebar',
    isTypeSidepanel: (state) => state.viewType === 'sidepanel',
  },

  state: (): State => ({
    isChatEnabled: false,
    isDisabled: false,
    isLauncherOpen: false,
    isNavMenuOpen: false,
    launcherCategory: null,
    viewType: null,
  }),
});

export default useLauncherStore;
